import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { reportLifecycleDiagnostic } from '../api/lifecycleDiagnostics';
import {
  browserAuthenticationApi,
  type AdminUserCreate,
  type AdminUserUpdate,
  type AuthenticationApi,
  type AuthUser,
  type CurrentSessionResponse,
} from './api';
import {
  AuthenticationContext,
  type AuthenticationMode,
  type AuthenticationRevalidationState,
  type AuthenticationState,
} from './authState';
import {
  authenticationErrorMessage,
  hasHttpStatus,
  isTransientAuthenticationError,
} from './errors';

interface AuthProviderProps extends PropsWithChildren {
  api?: AuthenticationApi | undefined;
  initialMode?: Extract<AuthenticationMode, 'local'> | undefined;
  initialPasswordResetToken?: string | null | undefined;
  onPasswordResetConsumed?: (() => void) | undefined;
}

const AUTH_BROADCAST_CHANNEL = 'flow-wizard-auth-v1';
type AuthBroadcastMessage = 'session-changed' | 'session-invalidated';
type AuthenticationProbeKind = 'background' | 'blocking' | 'sensitive';

interface ActiveAuthenticationProbe {
  controller: AbortController;
  generation: number;
  promise: Promise<void>;
}

type StartAuthenticationProbe = (
  kind: AuthenticationProbeKind,
  retryAttempt?: number,
) => Promise<void>;

const BACKGROUND_RETRY_DELAYS_MS = [1_000, 3_000] as const;
const IDLE_REVALIDATION: AuthenticationRevalidationState = {
  attempt: 0,
  message: null,
  status: 'idle',
};
const REVALIDATION_RETRY_MESSAGE =
  'Flow Wizard could not confirm the session. Your open work remains in place while it retries.';
const REVALIDATION_FAILED_MESSAGE =
  'Flow Wizard could not reconnect. Your open work remains in place; retry when the connection is available.';

function sessionView(response: CurrentSessionResponse) {
  return {
    absoluteExpiresAt: response.absolute_expires_at,
    idleExpiresAt: response.idle_expires_at,
    user: response.user,
  };
}

export function AuthProvider({
  api = browserAuthenticationApi,
  children,
  initialMode,
  initialPasswordResetToken = null,
  onPasswordResetConsumed,
}: AuthProviderProps) {
  const queryClient = useQueryClient();
  const initialAuthenticationMode: AuthenticationMode =
    initialPasswordResetToken !== null
      ? 'hosted-anonymous'
      : (initialMode ?? 'checking');
  const [mode, setMode] = useState<AuthenticationMode>(initialAuthenticationMode);
  const [session, setSession] =
    useState<AuthenticationState['session']>(null);
  const [error, setError] = useState<string | null>(null);
  const [revalidation, setRevalidation] =
    useState<AuthenticationRevalidationState>(IDLE_REVALIDATION);
  const [passwordResetToken, setPasswordResetToken] = useState(
    initialPasswordResetToken,
  );
  const [passwordResetComplete, setPasswordResetComplete] = useState(false);
  const requestGeneration = useRef(0);
  const initialProbeStarted = useRef(false);
  const modeRef = useRef<AuthenticationMode>(initialAuthenticationMode);
  const sessionRef = useRef<AuthenticationState['session']>(null);
  const broadcastChannel = useRef<BroadcastChannel | null>(null);
  const activeProbe = useRef<ActiveAuthenticationProbe | null>(null);
  const backgroundRetryTimer = useRef<number | null>(null);
  const startProbeRef = useRef<StartAuthenticationProbe>(() => Promise.resolve());

  const assignMode = useCallback((nextMode: AuthenticationMode) => {
    modeRef.current = nextMode;
    setMode(nextMode);
  }, []);

  const assignSession = useCallback(
    (nextSession: AuthenticationState['session']) => {
      sessionRef.current = nextSession;
      setSession(nextSession);
    },
    [],
  );

  const clearAuthenticatedWorkspace = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const clearBackgroundRetry = useCallback(() => {
    if (backgroundRetryTimer.current === null) return;
    window.clearTimeout(backgroundRetryTimer.current);
    backgroundRetryTimer.current = null;
  }, []);

  const cancelActiveProbe = useCallback(() => {
    requestGeneration.current += 1;
    activeProbe.current?.controller.abort();
    activeProbe.current = null;
    clearBackgroundRetry();
    setRevalidation(IDLE_REVALIDATION);
  }, [clearBackgroundRetry]);

  const becomeAnonymous = useCallback(
    (notifyOtherTabs: boolean) => {
      cancelActiveProbe();
      api.clearClientSession?.();
      clearAuthenticatedWorkspace();
      assignSession(null);
      setError(null);
      assignMode('hosted-anonymous');
      if (notifyOtherTabs) {
        broadcastChannel.current?.postMessage(
          'session-invalidated' satisfies AuthBroadcastMessage,
        );
      }
    },
    [
      api,
      assignMode,
      assignSession,
      cancelActiveProbe,
      clearAuthenticatedWorkspace,
    ],
  );

  const startProbe = useCallback<StartAuthenticationProbe>(
    (kind, retryAttempt = 0) => {
      const currentProbe = activeProbe.current;
      if (kind === 'background' && currentProbe !== null) {
        return currentProbe.promise;
      }

      if (
        kind === 'background' &&
        (modeRef.current !== 'hosted-authenticated' ||
          sessionRef.current === null)
      ) {
        return Promise.resolve();
      }

      if (kind === 'background') {
        reportLifecycleDiagnostic(
          'auth_revalidation_requested',
          'authentication',
          'started',
        );
      }

      cancelActiveProbe();
      setError(null);
      if (kind === 'blocking') {
        assignMode('checking');
      } else if (kind === 'sensitive') {
        api.clearClientSession?.();
        clearAuthenticatedWorkspace();
        assignSession(null);
        assignMode('hosted-suspended');
      } else {
        setRevalidation({
          attempt: retryAttempt,
          message: retryAttempt === 0 ? null : REVALIDATION_RETRY_MESSAGE,
          status: retryAttempt === 0 ? 'checking' : 'retrying',
        });
      }

      // Another tab can rotate the shared cookies. Discard the process-local CSRF
      // copy before every identity probe so the next write rereads the current cookie.
      api.clearClientSession?.();
      const generation = ++requestGeneration.current;
      const controller = new AbortController();
      const promise = (async () => {
        try {
          const identity = await api.identityMode(controller.signal);
          if (
            controller.signal.aborted ||
            generation !== requestGeneration.current
          ) {
            return;
          }
          if (identity.mode === 'local') {
            if (kind === 'background') {
              reportLifecycleDiagnostic(
                'auth_revalidation_completed',
                'authentication',
                'succeeded',
              );
            }
            api.clearClientSession?.();
            clearAuthenticatedWorkspace();
            assignSession(null);
            setRevalidation(IDLE_REVALIDATION);
            assignMode('local');
            return;
          }
          const response = await api.currentSession(controller.signal);
          if (
            controller.signal.aborted ||
            generation !== requestGeneration.current
          ) {
            return;
          }
          if (
            sessionRef.current !== null &&
            sessionRef.current.user.id !== response.user.id
          ) {
            clearAuthenticatedWorkspace();
          }
          assignSession(sessionView(response));
          if (kind === 'background') {
            reportLifecycleDiagnostic(
              'auth_revalidation_completed',
              'authentication',
              'succeeded',
            );
          }
          setError(null);
          setRevalidation(IDLE_REVALIDATION);
          assignMode('hosted-authenticated');
        } catch (reason: unknown) {
          if (
            controller.signal.aborted ||
            generation !== requestGeneration.current
          ) {
            return;
          }
          if (hasHttpStatus(reason, 401)) {
            if (kind === 'background') {
              reportLifecycleDiagnostic(
                'auth_revalidation_failed',
                'authentication',
                'failed',
              );
            }
            becomeAnonymous(false);
            return;
          }
          if (
            kind === 'background' &&
            modeRef.current === 'hosted-authenticated' &&
            sessionRef.current !== null &&
            isTransientAuthenticationError(reason)
          ) {
            reportLifecycleDiagnostic(
              'auth_revalidation_failed',
              'authentication',
              retryAttempt < BACKGROUND_RETRY_DELAYS_MS.length
                ? 'unavailable'
                : 'failed',
            );
            if (retryAttempt < BACKGROUND_RETRY_DELAYS_MS.length) {
              const nextAttempt = retryAttempt + 1;
              setRevalidation({
                attempt: nextAttempt,
                message: REVALIDATION_RETRY_MESSAGE,
                status: 'retrying',
              });
              backgroundRetryTimer.current = window.setTimeout(() => {
                backgroundRetryTimer.current = null;
                void startProbeRef.current('background', nextAttempt);
              }, BACKGROUND_RETRY_DELAYS_MS[retryAttempt]);
            } else {
              setRevalidation({
                attempt: retryAttempt,
                message: REVALIDATION_FAILED_MESSAGE,
                status: 'failed',
              });
            }
            return;
          }
          if (kind === 'background') {
            reportLifecycleDiagnostic(
              'auth_revalidation_failed',
              'authentication',
              'failed',
            );
          }
          api.clearClientSession?.();
          clearAuthenticatedWorkspace();
          assignSession(null);
          setRevalidation(IDLE_REVALIDATION);
          setError(
            authenticationErrorMessage(
              reason,
              'Flow Wizard could not determine whether sign in is required.',
            ),
          );
          assignMode('error');
        } finally {
          if (activeProbe.current?.generation === generation) {
            activeProbe.current = null;
          }
        }
      })();

      activeProbe.current = { controller, generation, promise };
      return promise;
    },
    [
      api,
      assignMode,
      assignSession,
      becomeAnonymous,
      cancelActiveProbe,
      clearAuthenticatedWorkspace,
    ],
  );

  useEffect(() => {
    startProbeRef.current = startProbe;
  }, [startProbe]);

  const retry = useCallback(() => {
    const kind =
      modeRef.current === 'hosted-authenticated' &&
      sessionRef.current !== null
        ? 'background'
        : 'blocking';
    void startProbe(kind);
  }, [startProbe]);

  useEffect(() => {
    if (
      initialMode !== undefined ||
      initialPasswordResetToken !== null ||
      initialProbeStarted.current
    ) {
      return;
    }
    initialProbeStarted.current = true;
    void startProbe('blocking');
  }, [initialMode, initialPasswordResetToken, startProbe]);

  useEffect(
    () => () => {
      requestGeneration.current += 1;
      activeProbe.current?.controller.abort();
      activeProbe.current = null;
      clearBackgroundRetry();
    },
    [clearBackgroundRetry],
  );

  useEffect(() => {
    if (api.subscribeToUnauthorized === undefined) return;
    return api.subscribeToUnauthorized(() => {
      becomeAnonymous(true);
    });
  }, [api, becomeAnonymous]);

  useEffect(() => {
    if (api.subscribeToSessionRefresh === undefined) return;
    return api.subscribeToSessionRefresh((expiries) => {
      const current = sessionRef.current;
      if (current === null) return;
      assignSession({
        ...current,
        absoluteExpiresAt: expiries.absoluteExpiresAt,
        idleExpiresAt: expiries.idleExpiresAt,
      });
    });
  }, [api, assignSession]);

  useEffect(() => {
    if (typeof window === 'undefined' || window.BroadcastChannel === undefined) {
      return;
    }
    const channel = new window.BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    broadcastChannel.current = channel;
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (event.data === 'session-invalidated') {
        becomeAnonymous(false);
      } else if (event.data === 'session-changed') {
        void startProbe('sensitive');
      }
    };
    return () => {
      broadcastChannel.current = null;
      channel.close();
    };
  }, [becomeAnonymous, startProbe]);

  useEffect(() => {
    if (mode !== 'hosted-authenticated' || session === null) return;
    const deadline = Math.min(
      Date.parse(session.idleExpiresAt),
      Date.parse(session.absoluteExpiresAt),
    );
    const delay = Number.isFinite(deadline) ? Math.max(0, deadline - Date.now()) : 0;
    const timer = window.setTimeout(
      () => void startProbe('background'),
      Math.min(delay, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [mode, session, startProbe]);

  useEffect(() => {
    if (mode !== 'hosted-authenticated') return;
    let queued = false;
    const revalidateVisibleSession = () => {
      if (document.visibilityState !== 'visible' || queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        void startProbe('background');
      });
    };
    window.addEventListener('focus', revalidateVisibleSession);
    document.addEventListener('visibilitychange', revalidateVisibleSession);
    return () => {
      window.removeEventListener('focus', revalidateVisibleSession);
      document.removeEventListener('visibilitychange', revalidateVisibleSession);
    };
  }, [mode, startProbe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await api.login(email, password);
      cancelActiveProbe();
      clearAuthenticatedWorkspace();
      setError(null);
      assignSession(sessionView(response));
      assignMode('hosted-authenticated');
      broadcastChannel.current?.postMessage(
        'session-changed' satisfies AuthBroadcastMessage,
      );
    },
    [
      api,
      assignMode,
      assignSession,
      cancelActiveProbe,
      clearAuthenticatedWorkspace,
    ],
  );

  const logout = useCallback(async () => {
    cancelActiveProbe();
    try {
      await api.logout();
    } catch (reason) {
      if (!hasHttpStatus(reason, 401)) throw reason;
    }
    becomeAnonymous(true);
  }, [api, becomeAnonymous, cancelActiveProbe]);

  const requestPasswordReset = useCallback(
    (email: string) => api.requestPasswordReset(email),
    [api],
  );

  const confirmPasswordReset = useCallback(
    async (password: string) => {
      if (passwordResetToken === null) {
        throw new Error('This password reset link is missing its token.');
      }
      await api.confirmPasswordReset(passwordResetToken, password);
      cancelActiveProbe();
      api.clearClientSession?.();
      clearAuthenticatedWorkspace();
      setPasswordResetToken(null);
      setPasswordResetComplete(true);
      onPasswordResetConsumed?.();
      assignSession(null);
      assignMode('hosted-anonymous');
      broadcastChannel.current?.postMessage(
        'session-invalidated' satisfies AuthBroadcastMessage,
      );
    },
    [
      api,
      assignMode,
      assignSession,
      cancelActiveProbe,
      clearAuthenticatedWorkspace,
      onPasswordResetConsumed,
      passwordResetToken,
    ],
  );

  const clearPasswordReset = useCallback(() => {
    cancelActiveProbe();
    setPasswordResetToken(null);
    setPasswordResetComplete(false);
    onPasswordResetConsumed?.();
    assignSession(null);
    setError(null);
    assignMode('hosted-anonymous');
  }, [assignMode, assignSession, cancelActiveProbe, onPasswordResetConsumed]);

  const createUser = useCallback(
    (payload: AdminUserCreate) => api.createUser(payload),
    [api],
  );

  const listUsers = useCallback(() => api.listUsers(), [api]);

  const adoptCurrentUserUpdate = useCallback(
    (updated: AuthUser) => {
      if (session?.user.id !== updated.id) return;
      if (!updated.is_active) {
        becomeAnonymous(true);
        return;
      }
      const current = sessionRef.current;
      if (current !== null) {
        assignSession({ ...current, user: updated });
        broadcastChannel.current?.postMessage(
          'session-changed' satisfies AuthBroadcastMessage,
        );
      }
    },
    [assignSession, becomeAnonymous, session?.user.id],
  );

  const updateUser = useCallback(
    async (accountId: string, payload: AdminUserUpdate) => {
      const updated = await api.updateUser(accountId, payload);
      adoptCurrentUserUpdate(updated);
      return updated;
    },
    [adoptCurrentUserUpdate, api],
  );

  const setUserEnabled = useCallback(
    async (accountId: string, enabled: boolean) => {
      const updated = await api.setUserEnabled(accountId, enabled);
      adoptCurrentUserUpdate(updated);
      return updated;
    },
    [adoptCurrentUserUpdate, api],
  );

  const value = useMemo<AuthenticationState>(
    () => ({
      clearPasswordReset,
      confirmPasswordReset,
      createUser,
      error,
      listUsers,
      login,
      logout,
      mode,
      passwordResetComplete,
      passwordResetToken,
      revalidation,
      requestPasswordReset,
      retry,
      session,
      setUserEnabled,
      updateUser,
    }),
    [
      clearPasswordReset,
      confirmPasswordReset,
      createUser,
      error,
      listUsers,
      login,
      logout,
      mode,
      passwordResetComplete,
      passwordResetToken,
      revalidation,
      requestPasswordReset,
      retry,
      session,
      setUserEnabled,
      updateUser,
    ],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
}
